import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import withSubscriptionAwareness from '../../hoc/withSubscriptionAwareness';
import { useAccount } from '../../hooks/useAccount';
import { useBriefsOverview, useCreateBrief } from '../../hooks/useBriefs';
import { useBriefStoriesPreview } from '../../hooks/useBriefStories';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import DashboardPageLayout from '../../layouts/dashboard/dashboardPageLayout';
import { Brief } from '../../services/models/brief';
import { BriefKeywordInput } from '../../services/models/brief-creation-request';
import { BriefKeywordType } from '../../services/models/brief-keyword-type';
import { BriefLanguage } from '../../services/models/brief-language';
import { BriefStatus } from '../../services/models/brief-status';
import { FeatureFlag } from '../../services/models/feature-flags';
import DefineReportStep from './components/define-brief-step/DefineBriefStep';
import EditSidebar from './components/edit-sidebar/EditSidebar';
import NewBriefWizardStepper, {
  AddBriefWizardStep,
} from './components/new-brief-wizard-stepper/NewBriefWizardStepper';
import RefineReportStep from './components/refine-brief-step/RefineBriefStep';
import SetBriefNameStep from './components/set-brief-name-step/SetBriefNameStep';

const NeutralRoute = '/dashboard/brief/new';
const DefineRoute = '/dashboard/brief/new/define';
const RefineRoute = '/dashboard/brief/new/refine';
const NameRoute = '/dashboard/brief/new/complete';

const AddBriefPage: FunctionComponent = () => {
  const history = useHistory();
  const location = useLocation();

  const { account: currentAccount } = useAccount();
  const account = currentAccount!;
  const { briefs: briefItems, loading: isFetchingBriefs } = useBriefsOverview();
  const briefs = { items: briefItems };
  const { createBrief, isCreating } = useCreateBrief();
  const { storiesPreview, isFetchingPreview, refreshPreview } = useBriefStoriesPreview();

  const [step, setStep] = useState(AddBriefWizardStep.DefineScreen);
  const newReportShowPreviewScreenFlag = useFeatureFlag(FeatureFlag.NewReportShowPreviewScreen);

  useEffect(() => {
    switch (location.pathname) {
      case RefineRoute:
        setStep(AddBriefWizardStep.RefineScreen);
        break;
      case NameRoute:
        setStep(AddBriefWizardStep.NameScreen);
        break;
      default:
        setStep(AddBriefWizardStep.DefineScreen);
        break;
    }
  }, [location, setStep]);

  const [draftBrief, setDraftBrief] = useState<Brief | null>(null);
  const draftBriefIdRef = useRef<number | null>(null);

  useEffect(() => {
    const draftBrief = (briefItems || []).find(x => x.status === BriefStatus.Draft) || null;
    setDraftBrief(draftBrief);
  }, [briefItems, setDraftBrief]);

  useEffect(() => {
    if (draftBrief && step === AddBriefWizardStep.DefineScreen) {
      history.push(RefineRoute);
    }

    if (draftBriefIdRef.current !== null) {
      const brief = (briefItems || []).find(brief => brief.id === draftBriefIdRef.current);

      if (brief && brief.status !== BriefStatus.Draft) {
        history.push(`/dashboard/brief/${draftBriefIdRef.current}`);
      }
    } else if (draftBrief) {
      draftBriefIdRef.current = draftBrief.id;
    }
  }, [draftBrief, briefItems, history, step]);

  const capitalize = (s: string) => {
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const handleDefineStepCompletion = async (keywords: BriefKeywordInput[]) => {
    // TODO(Jose): revise suffix strategy for report naming
    // const source = ['Briefing', 'News', 'Clippings'];
    // const item = source[Math.floor(Math.random() * source.length)];
    const reportName = capitalize(
      keywords.filter(x => x.type === BriefKeywordType.Topic)[0].keyword
    );

    const newBrief = await createBrief({
      name: reportName,
      keywords: keywords,
      // TODO(Jose): revise brief draft strategy moving forward
      draft: false,
      language: BriefLanguage.English,
    });

    // Navigate from the mutation result id (legacy watched the store for a
    // matching brief).
    if (newBrief) {
      history.push(`/dashboard/brief/${newBrief.id}`);
    }
  };

  const handleRefineStepCompletion = () => {
    history.push(NameRoute);
  };

  const isLoading = () => {
    return !!isCreating || !!isFetchingBriefs || !!draftBrief?.isPublishing;
  };

  if (briefs.items && account.product) {
    const briefsExceeded =
      briefs.items.filter(x => x.status === 'ACTIVE').length === account.product.features.briefs;
    const notActive = !account.subscription.active;

    if (briefsExceeded) {
      return <Redirect to="/dashboard/upsell/report_count" />;
    } else if (notActive) {
      return <Redirect to="/dashboard/upsell/not_active" />;
    }

    if (location.pathname === NeutralRoute) {
      return <Redirect to={DefineRoute} />;
    }
  }

  // The legacy EditSidebar renders `fixed right-0 w-[296px]` on the refine
  // step, so the wizard column must reserve that width (at md+, matching the
  // sidebar's breakpoint) or the step content renders underneath it.
  const showLegacySidebar =
    !newReportShowPreviewScreenFlag && step === AddBriefWizardStep.RefineScreen;

  return (
    <DashboardPageLayout title="Add New Brief">
      {/* DashboardPageLayout's content area is a flex row; this column keeps
          the stepper stacked above the step content. */}
      <div
        className={`flex min-w-0 flex-1 flex-col overflow-y-auto ${
          showLegacySidebar ? 'md:pr-[296px]' : ''
        }`}
      >
        <NewBriefWizardStepper isWorking={isLoading()} step={step} />

        <DefineReportStep
          active={step === AddBriefWizardStep.DefineScreen}
          showCancelAction={!(account.metadata?.totalReportsCount === 0)}
          onComplete={keywords => handleDefineStepCompletion(keywords)}
        />

        <RefineReportStep
          active={step === AddBriefWizardStep.RefineScreen}
          draftBrief={draftBrief}
          storiesPreview={storiesPreview}
          isFetchingPreview={isFetchingPreview}
          refreshPreview={refreshPreview}
          onComplete={() => handleRefineStepCompletion()}
        />

        <SetBriefNameStep active={step === AddBriefWizardStep.NameScreen} draftBrief={draftBrief} />

        {showLegacySidebar && (
          <EditSidebar
            active={true}
            draftBrief={draftBrief}
            refreshPreview={refreshPreview}
            onComplete={() => handleRefineStepCompletion()}
          />
        )}
      </div>
    </DashboardPageLayout>
  );
};

export default withSubscriptionAwareness(AddBriefPage);
